import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/context/LanguageContext';
import { submitJobApplication, uploadCareerCv } from '@/services/careerService';

interface JobApplyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobTitle: string;
}

const JobApplyDialog: React.FC<JobApplyDialogProps> = ({
  open,
  onOpenChange,
  jobId,
  jobTitle,
}) => {
  const { tl } = useLanguage();
  const { toast } = useToast();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [extraInfo, setExtraInfo] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setExtraInfo('');
    setCvFile(null);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!cvFile) throw new Error('CV required');
      const cvUrl = await uploadCareerCv(cvFile);
      return submitJobApplication({
        jobId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        cvUrl,
        extraInfo: extraInfo.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast({
        title: tl('Candidature envoyée', 'Application sent', 'Candidatura inviata'),
        description: tl(
          'Nous avons bien reçu votre dossier.',
          'We have received your application.',
          'Abbiamo ricevuto la sua candidatura.'
        ),
      });
      reset();
      onOpenChange(false);
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: tl('Erreur', 'Error', 'Errore'),
        description: tl(
          'Impossible d’envoyer la candidature. Réessayez.',
          'Could not send the application. Please try again.',
          'Impossibile inviare la candidatura. Riprovi.'
        ),
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !email.trim() || !cvFile) {
      toast({
        title: tl('Champs requis', 'Required fields', 'Campi obbligatori'),
        description: tl(
          'Remplissez nom, prénom, téléphone, email et CV.',
          'Fill in last name, first name, phone, email and CV.',
          'Compili cognome, nome, telefono, email e CV.'
        ),
        variant: 'destructive',
      });
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="max-h-[90dvh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto sm:w-full">
        <DialogHeader>
          <DialogTitle>
            {tl('Postuler', 'Apply', 'Candidati')} — {jobTitle}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-1">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="lastName">{tl('Nom', 'Last name', 'Cognome')}</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="h-11"
                autoComplete="family-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="firstName">{tl('Prénom', 'First name', 'Nome')}</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="h-11"
                autoComplete="given-name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{tl('Téléphone', 'Phone', 'Telefono')}</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-11"
              autoComplete="tel"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <Label>{tl('CV attaché', 'Attach CV', 'Allega CV')}</Label>
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/30 px-4 py-6 text-center transition-colors hover:border-furniture-gold/50">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {cvFile
                  ? cvFile.name
                  : tl('PDF, DOC ou image', 'PDF, DOC or image', 'PDF, DOC o immagine')}
              </span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,image/*"
                className="hidden"
                onChange={(e) => setCvFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="extra">
              {tl(
                'Plus d’informations (optionnel)',
                'More information (optional)',
                'Altre informazioni (opzionale)'
              )}
            </Label>
            <Textarea
              id="extra"
              rows={3}
              value={extraInfo}
              onChange={(e) => setExtraInfo(e.target.value)}
              className="resize-none"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {tl('Annuler', 'Cancel', 'Annulla')}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="bg-furniture-gold text-black hover:bg-furniture-gold-hover"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {tl('Envoi…', 'Sending…', 'Invio…')}
                </>
              ) : (
                tl('Envoyer', 'Submit', 'Invia')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobApplyDialog;
